/**
 * Что происходит с лицензией машины — и когда об этом пора предупредить.
 *
 * ## Где кабинет вообще видит лицензию
 *
 * ⚠️ **Ровно в одном месте: `GET /admin/machines/{number}`.** Это единственный
 * лицензионный маршрут, авторизованный в домене СВОЕЙ компании; `GET /admin/licenses`
 * и `GET /admin/machines` — `system`-only даже с `X-Company-Id` (спека гейтвея,
 * «`system` domain only, even with `X-Company-Id`»), то есть владельцу отвечают `403`.
 * А номер машины нужен, чтобы туда вообще постучаться, и в `DeviceSummary` его нет:
 * список устройств `/control/devices` не несёт ни `machine_number`, ни `hardware_id`.
 *
 * Отсюда прямое следствие, которое стоит знать, прежде чем искать баг: **кабинет не
 * может показать, когда истекает лицензия у уже подключённого терминала.** Он видит её
 * только в момент подключения, когда номер машины в руках — с QR-кода или введён
 * руками. Чтобы предупреждать по всему парку, нужен маршрут: либо `license` в строке
 * устройства, либо `GET /admin/licenses?company_id=` в домене компании.
 *
 * ## Почему порог — тридцать дней
 *
 * Лицензия продлевается не нажатием кнопки, а разговором с поставщиком, и разговор
 * этот занимает недели. Предупреждение за неделю — это уже не предупреждение, а
 * сообщение о случившемся. Месяц — самый короткий срок, за который решение успевает
 * пройти.
 */
import type {MachineLicense} from '@/entities/machine/types';

/** За сколько дней до конца лицензия считается заканчивающейся. */
export const EXPIRY_WARNING_DAYS = 30;

export type LicenseState =
  /** Лицензии нет вовсе — у машины это означает сбой выше по течению. */
  | {kind: 'missing'}
  /** Приостановлена платформой. Дата тут ни при чём. */
  | {kind: 'suspended'}
  | {kind: 'expired'; until: string | null}
  | {kind: 'expiring'; until: string; daysLeft: number}
  | {kind: 'active'; until: string; daysLeft: number}
  /** `valid_until` пуст — бессрочная. Не «неизвестно» и не «истекла». */
  | {kind: 'perpetual'};

/** Полночь того календарного дня, в который попадает момент. Локальная, как всё в кабинете. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Сколько КАЛЕНДАРНЫХ дней осталось: лицензия, кончающаяся сегодня в 23:59, — это
 * «сегодня», а не «через 0,97 дня». Считается по полуночам, поэтому переход через
 * летнее время не сдвигает ответ на единицу.
 */
export function daysUntil(untilMillis: number, now: number): number {
  const DAY = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(untilMillis) - startOfDay(now)) / DAY);
}

/**
 * Разбор `valid_until`. Пустая строка — бессрочная лицензия (так это описано в спеке),
 * непарсящаяся — `null`: сервер прислал что-то, чего мы не понимаем, и врать про дату
 * хуже, чем её не показать.
 */
function parseUntil(value: string): number | null | 'perpetual' {
  if (!value) {
    return 'perpetual';
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Состояние лицензии на момент `now`.
 *
 * `status` и дата могут не совпадать, и тогда выигрывает та из них, которая говорит
 * «хуже»: `status` — это приговор сервера, по нему терминал и перестанет работать, а
 * прошедшая дата при `ACTIVE` — это несведённая строка, о которой владельцу всё равно
 * надо знать заранее, а не в день отключения.
 */
export function licenseState(
  license: MachineLicense | null | undefined,
  now: number = Date.now(),
): LicenseState {
  if (!license) {
    return {kind: 'missing'};
  }

  const status = (license.status ?? '').toUpperCase();
  if (status === 'SUSPENDED') {
    return {kind: 'suspended'};
  }

  const until = parseUntil(license.valid_until);
  if (until === 'perpetual') {
    // Бессрочную не может отменить ничто, кроме статуса, — а статус уже проверен.
    return status === 'EXPIRED' ? {kind: 'expired', until: null} : {kind: 'perpetual'};
  }
  if (until === null) {
    return status === 'EXPIRED'
      ? {kind: 'expired', until: null}
      : {kind: 'perpetual'};
  }

  const daysLeft = daysUntil(until, now);
  if (status === 'EXPIRED' || daysLeft < 0) {
    return {kind: 'expired', until: license.valid_until};
  }
  return daysLeft <= EXPIRY_WARNING_DAYS
    ? {kind: 'expiring', until: license.valid_until, daysLeft}
    : {kind: 'active', until: license.valid_until, daysLeft};
}

/** Состояния, о которых надо СКАЗАТЬ, а не просто показать дату мелким шрифтом. */
export function isLicenseAlarming(state: LicenseState): boolean {
  return (
    state.kind === 'expired' ||
    state.kind === 'expiring' ||
    state.kind === 'suspended' ||
    state.kind === 'missing'
  );
}

/**
 * Мест в лицензии — занятых из всех, и не осталось ли их вовсе.
 *
 * `max_devices: 0` встречается и означает «без ограничения», а не «мест нет»: ноль как
 * потолок отключил бы лицензию, которая на самом деле безлимитная.
 */
export function seatState(license: MachineLicense | null | undefined): {
  used: number;
  max: number;
  unlimited: boolean;
  full: boolean;
} | null {
  if (!license) {
    return null;
  }
  const max = license.max_devices ?? 0;
  const used = license.used_devices ?? 0;
  return {used, max, unlimited: max <= 0, full: max > 0 && used >= max};
}
